import { OttUserAnonymousLoginAction, AppTokenStartSessionAction } from '@kaltura-ott/ott-client-types/api/types';
import { HouseholdDeviceGetErrorCodeEnum, kalturaClient } from '@kaltura-ott/tvpil';
import { MiddlewareFunc } from '@kaltura-ott/tvpil';
import { throwError, BehaviorSubject, Subject } from 'rxjs';
import { switchMap, catchError, first, takeUntil, tap } from 'rxjs/operators';
import { container } from 'tsyringe';

import {
  createRefreshKsDeferred,
  isRefreshInProcess,
  rejectRefreshKsDeferred,
  resolveRefreshKsDeferred,
} from '../rhino/phoenix/network/deferRefreshKs';
import { ErrorStore } from '../stores/error/error-store';
import type { TransactionStore } from '../stores/transaction/transaction-store';
import type { LoginStore } from '../stores/user/login-store';

let requestCancelSubject = new Subject();
const ksSubject: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
const updatingKsSubject: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(false);
const ks = ksSubject.asObservable();
const updatingKs = updatingKsSubject.asObservable();

export function refreshToken() {
  if (isRefreshInProcess()) {
    return;
  }

  createRefreshKsDeferred();

  ksSubject.next('');

  middle(container.resolve<LoginStore>('loginStore'));
}

const middle = async (loginStore: LoginStore) => {
  if (!updatingKsSubject.getValue()) {
    updatingKsSubject.next(true);
    try {
      const ks = await loginStore.getNewKs();
      updatingKsSubject.next(false);
      if (ks) {
        ksSubject.next(ks);
      }

      try {
        // KUXEXT-20971: refresh access token
        await loginStore?.updateAccessToken();

        const transactionStore = container.resolve<TransactionStore>('transactionStore');
        await transactionStore?.loadMainSubscriptions();
      } catch (e) {
        console.log('Invalid access token', e);
      }

      resolveRefreshKsDeferred();
    } catch (e) {
      // remove all waiting requests
      requestCancelSubject.complete();
      requestCancelSubject = new Subject();

      updatingKsSubject.next(false);

      rejectRefreshKsDeferred(e);

      const errorStore = container.resolve(ErrorStore);
      errorStore?.clearAllApiError();

      // starting new flow
      await loginStore?.logoutUser(false);
      if (loginStore.isSilentLogin) {
        await loginStore.loginSilentUser();
      }
    }
  }
};

export const createRenewKsMiddleware =
  (loginStore: LoginStore): MiddlewareFunc =>
  (fn, ...args) =>
  (source) => {
    return updatingKs.pipe(
      // remove all waiting requests  - if appTokenStartSession is not success
      takeUntil(requestCancelSubject),
      first(
        // wait while the update process is in progress.
        // All actions must wait except related to KS update
        (isUpdatingKs) =>
          !isUpdatingKs ||
          args[0] instanceof OttUserAnonymousLoginAction ||
          args[0] instanceof AppTokenStartSessionAction,
      ),
      tap(() => {
        kalturaClient();
      }),
      switchMap(() => {
        return source.pipe(
          catchError((error) => {
            if (error?.code === HouseholdDeviceGetErrorCodeEnum.ExpiredKs) {
              ksSubject.next('');
              createRefreshKsDeferred();
              middle(loginStore);
              return ks.pipe(
                takeUntil(requestCancelSubject),
                first((resp) => !!resp),
                switchMap(() => {
                  kalturaClient();
                  //executing an unsuccessful query request
                  return fn(...args);
                }),
              );
            }
            return throwError(error);
          }),
        );
      }),
    );
  };
